import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import axios from 'axios';
// import { v4 as uuidv4 } from 'uuid'; // Not needed for now
import './styles/ChatLandingPage.css';
import API_BASE_URL from '../config/api';

const ChatLandingPage = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [draftCampaign, setDraftCampaign] = useState(null);
  // const [sessionId] = useState(() => uuidv4()); // Unused for now
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const messagesEndRef = useRef(null);

  // Check if user is logged in
  const isLoggedIn = !!localStorage.getItem('authToken');

  // Scroll to bottom when messages change
  useEffect(() => {
    if (!hasInteracted) return; // Skip auto-scroll until user interacts
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, hasInteracted]);

  // Load chat history from localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem('chatHistory');
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory);
        setMessages(parsed);
      } catch (e) {
        console.error('Failed to load chat history:', e);
      }
    } else {
      // Show welcome message
      setMessages([{
        role: 'assistant',
        content: '👋 Hi! I’ll create an automated outreach campaign that reaches the right audience.\n\nTell me:\n\n• What you’re offering\n• Who you want to reach\n• Where they’re based (or state “anywhere”)\n\nThen I’ll create a campaign, feel free to suggest refinements ✨\n\nYou can reach any audience you can think of. Here are a few examples to get you thinking.\n\n• Fitness creators — promote supplements, coaching programs, or fitness apps\n• CEOs — pitch services, partnerships, or high-value B2B solutions\n• Venture capitalists — introduce startups, raise investment, or share deal flow\n• Indie game developers — promote tools, publishing services, or collaborations\n• Crypto traders — share analytics tools, newsletters, or trading platforms\n• Real estate investors — pitch deals, financing, or property services\n• Streamers — offer sponsorships, software, or brand collaborations\n• Comedians — promote events, merch, or content partnerships\n• Coaches — sell high-ticket programs, platforms, or lead generation\n• Educators — promote courses, tools, or educational resources\n\nOur outreach-optimised AI writes high-performance outreach, identifies relevant people based on their interests, and prepares campaigns automatically, with no setup required. Then the outreach is sent from your own connected Gmail account.\n\nReach the right people across niches, communities, and interests, at scale. No setup. Completely automated. Your entire campaign could be underway in under 5 minutes from now.\n\n• No email lists to build\n• No manual research\n• No complicated technical setup\n\nBuilt for natural, personal outreach, your outreach is sent in a way that looks and feels like genuine one-to-one communication, not mass messaging or blasts.\n\nThis helps campaigns:\n\n• Feel personal\n• Get responses\n• Scale without friction\n\nSkip days of manual work:\n\n• No days wasted on manual work\n• No days pulling together spreadsheets\n• No days copying and pasting emails\n• No days researching audiences\n• No days jumping between platforms\n• No days writing drafts\n• No days setting up complicated email platforms\n\nYour entire campaign could be underway in under 5 minutes from now.'
      }]);
    }
  }, []);

  // Save messages to localStorage
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('chatHistory', JSON.stringify(messages));
    }
  }, [messages]);

  // Load draft campaign from localStorage
  useEffect(() => {
    const savedDraft = localStorage.getItem('draftCampaign');
    if (savedDraft) {
      try {
        setDraftCampaign(JSON.parse(savedDraft));
      } catch (e) {
        console.error('Failed to load draft campaign:', e);
      }
    }
  }, []);

  const handleSendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = { role: 'user', content: input.trim() };
    setMessages(prev => [...prev, userMessage]);
    setHasInteracted(true);
    setInput('');
    setIsLoading(true);

    try {
      console.log('🤖 [ChatGPT] Sending request to backend...');
      
      const response = await axios.post(`${API_BASE_URL}/api/chatgpt/generate`, {
        messages: [...messages, userMessage]
      });

      console.log('✅ [ChatGPT] Response received:', response.data);

      if (response.data.success && response.data.data) {
        const { searchTerm, emailSubject, emailBody } = response.data.data;

        // Format assistant response
        const assistantMessage = {
          role: 'assistant',
          content: `Great! Here's a campaign idea for you:\n\n📍 **Search Term:** ${searchTerm}\n📧 **Subject:** ${emailSubject}\n\n**Email:**\n${emailBody}\n\n✨ Click "Use This Idea" below to create your campaign!`
        };

        setMessages(prev => [...prev, assistantMessage]);

        // Save draft campaign
        const draft = { searchTerm, emailSubject, emailBody };
        setDraftCampaign(draft);
        localStorage.setItem('draftCampaign', JSON.stringify(draft));

        console.log('💾 Draft campaign saved:', draft);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('❌ [ChatGPT] Error:', error);
      
      const errorMessage = {
        role: 'assistant',
        content: '❌ Sorry, I encountered an error. Please try again or rephrase your request.'
      };
      setMessages(prev => [...prev, errorMessage]);
      message.error('Failed to generate campaign. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleUseIdea = () => {
    if (!draftCampaign) {
      message.warning('No campaign idea available. Chat with me first!');
      return;
    }

    if (!isLoggedIn) {
      // Show login modal instead of redirecting immediately
      setShowLoginModal(true);
      return;
    }

    // User is logged in, save to sessionStorage and navigate
    navigateToCampaignCreation();
  };

  // Navigate to campaign creation with pre-fill
  const navigateToCampaignCreation = () => {
    // Store draft for CreateCampaign to read
    sessionStorage.setItem('preFillCampaign', JSON.stringify(draftCampaign));
    
    // Clear chat state
    localStorage.removeItem('chatHistory');
    localStorage.removeItem('draftCampaign');
    
    console.log('🎨 [ChatGPT] Navigating to campaign creation with pre-fill data');
    
    // Navigate to campaign creation (AFC uses /campaigns)
    navigate('/campaigns?openModal=true');
  };

  // Handle Google login from modal
  const handleGoogleLogin = () => {
    // Store current state before redirect for post-login restoration
    sessionStorage.setItem('chatState', JSON.stringify({
      messages,
      draftCampaign,
      returnTo: 'chat-campaign-creation'
    }));
    
    console.log('🎨 [ChatGPT] Saving chat state for post-login restoration');
    
    // Redirect to OAuth WITHOUT mainUserToken (this is USER LOGIN, not Gmail addition)
    window.location.href = `${API_BASE_URL}/api/auth/google`;
  };

  // Removed handleLogin() - now using handleGoogleLogin() directly

  return (
    <div className="chat-landing-page">
      {/* Header */}
      <div className="chat-header">
        <div className="chat-header-left">
          <div className="chat-title">Automated Email Outreach</div>
        </div>
        <div className="chat-header-right">
          {isLoggedIn ? (
            <button className="header-btn" onClick={() => navigate('/dashboard')}>
              Dashboard
            </button>
          ) : (
            <button className="header-btn" onClick={() => setShowLoginModal(true)}>
              Log in
            </button>
          )}
        </div>
      </div>

      {/* Chat Container */}
      <div className="chat-container">
        <div className="messages-wrapper">
          {messages.map((msg, index) => (
            <div key={index} className={`message-block ${msg.role}`}>
              <div className="message-content">{msg.content}</div>
            </div>
          ))}

          {isLoading && (
            <div className="message-block assistant">
              <div className="message-content">
                <span className="typing-indicator">...</span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Use This Idea Button */}
        {draftCampaign && !isLoading && (
          <div className="action-button-container">
            <button className="use-idea-button" onClick={handleUseIdea}>
              ✨ Use This Idea
            </button>
          </div>
        )}

        {/* Input */}
        <div className="input-container">
          <textarea
            className="chat-input"
            placeholder="Message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            rows={1}
            disabled={isLoading}
          />
          <button
            className="send-button"
            onClick={handleSendMessage}
            disabled={isLoading || !input.trim()}
          >
            Send
          </button>
        </div>
      </div>

      {/* Login Modal */}
      {showLoginModal && (
        <div className="modal-overlay" onClick={() => setShowLoginModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowLoginModal(false)}>
              ×
            </button>
            <h2>Login Required</h2>
            <p>Sign in with Google to create your campaign</p>
            <button className="google-login-btn" onClick={handleGoogleLogin}>
              <img 
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" 
                alt="Google" 
              />
              Sign in with Google
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="chat-footer">
        <div>Powered by GPT-4o-mini • Your AI Campaign Assistant</div>
        <div className="footer-links">
          <a href="/privacy-policy">Privacy</a> • <a href="/terms-of-service">Terms</a>
        </div>
      </div>
    </div>
  );
};

export default ChatLandingPage;
